/**
 * Deck import/export helpers.
 *
 * Formats (see `.cursor/skills/deck-serialization/SKILL.md`):
 * - Text: human-readable sections (Legend, Champion, MainDeck, Battlefields, Runes, Sideboard, optional Extras, Notes)
 * - TTS: space-separated SET-NUM-VARIANT codes, quantity by repetition
 * - JSON: structured deck object with legend, champion, mainDeck, battlefields, runes, sideboard, extras, notes
 * - Base64: JSON deck encoded as UTF-8 base64 string
 */

function idFrom(entry) {
  return entry?.id ?? entry?.card_id ?? '';
}

function qtyFrom(entry) {
  return entry?.quantity ?? 1;
}

function nameFrom(entry) {
  return entry?.name ?? '';
}

export function emitText(deck) {
  const lines = [];
  if (deck.name) lines.push(`Deck: ${deck.name}`);
  if (deck.legend) {
    const name = deck.legend.name ?? deck.legend.id ?? idFrom(deck.legend);
    lines.push(`Legend:\n1 ${name}`);
  }
  if (deck.champion) {
    const name = deck.champion.name ?? deck.champion.id ?? idFrom(deck.champion);
    lines.push(`Champion:\n1 ${name}`);
  }
  lines.push('MainDeck:');
  for (const c of deck.mainDeck || []) {
    const q = qtyFrom(c);
    const name = nameFrom(c) || idFrom(c);
    if (name) lines.push(`${q} ${name}`);
  }
  lines.push('Battlefields:');
  for (const c of deck.battlefields || []) {
    const name = nameFrom(c) || idFrom(c);
    if (name) lines.push(`1 ${name}`);
  }
  lines.push('Runes:');
  for (const c of deck.runes || []) {
    const q = qtyFrom(c);
    const name = nameFrom(c) || idFrom(c);
    if (name) lines.push(`${q} ${name}`);
  }
  lines.push('Sideboard:');
  for (const c of deck.sideboard || []) {
    const q = qtyFrom(c);
    const name = nameFrom(c) || idFrom(c);
    if (name) lines.push(`${q} ${name}`);
  }
  if (deck.extras && deck.extras.length) {
    lines.push('Extras:');
    for (const c of deck.extras || []) {
      const q = qtyFrom(c);
      const name = nameFrom(c) || idFrom(c);
      if (name) lines.push(`${q} ${name}`);
    }
  }
  if (deck.notes) lines.push(`Notes: ${deck.notes}`);
  return lines.join('\n');
}

export function parseText(text) {
  const deck = { mainDeck: [], battlefields: [], runes: [], sideboard: [] };
  let current = null;
  for (const line of (text || '').split(/\r?\n/)) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    if (/^Deck:\s*/i.test(trimmed)) {
      deck.name = trimmed.replace(/^Deck:\s*/i, '').trim();
      continue;
    }
    if (/^Legend:\s*$/i.test(trimmed)) {
      current = 'legend';
      continue;
    }
    if (/^Champion:\s*$/i.test(trimmed)) {
      current = 'champion';
      continue;
    }
    if (/^MainDeck:\s*$/i.test(trimmed)) {
      current = 'mainDeck';
      continue;
    }
    if (/^Battlefields?:\s*$/i.test(trimmed)) {
      current = 'battlefields';
      continue;
    }
    if (/^Runes:\s*$/i.test(trimmed)) {
      current = 'runes';
      continue;
    }
    if (/^Sideboard:\s*$/i.test(trimmed)) {
      current = 'sideboard';
      continue;
    }
    if (/^Extras:\s*$/i.test(trimmed)) {
      current = 'extras';
      continue;
    }
    if (/^Notes:\s*/i.test(trimmed)) {
      deck.notes = trimmed.replace(/^Notes:\s*/i, '').trim();
      continue;
    }
    const match = trimmed.match(/^(\d+)\s+(.+)$/);
    const qty = match ? parseInt(match[1], 10) : 1;
    const nameOrId = (match ? match[2] : trimmed).trim();
    const entry = { name: nameOrId, quantity: qty };
    if (current === 'legend') deck.legend = { name: nameOrId };
    else if (current === 'champion') deck.champion = { name: nameOrId };
    else if (current === 'mainDeck') deck.mainDeck.push(entry);
    else if (current === 'battlefields') deck.battlefields.push({ name: nameOrId });
    else if (current === 'runes') deck.runes.push(entry);
    else if (current === 'sideboard') deck.sideboard.push(entry);
    else if (current === 'extras') {
      if (!deck.extras) deck.extras = [];
      deck.extras.push(entry);
    }
  }
  return deck;
}

/** TTS: space-separated SET-NUM-VARIANT (e.g. OGN-166-1 OGN-036-1). */
export function emitTTS(deck) {
  const parts = [];
  if (deck.legend?.id) parts.push(deck.legend.id);
  if (deck.champion?.id) parts.push(deck.champion.id);
  for (const c of deck.mainDeck || []) {
    const id = idFrom(c);
    const q = qtyFrom(c);
    for (let i = 0; i < q; i++) parts.push(id + (i > 0 ? `-${i}` : ''));
  }
  for (const c of deck.battlefields || []) {
    parts.push(idFrom(c));
  }
  for (const c of deck.runes || []) {
    const id = idFrom(c);
    const q = qtyFrom(c);
    for (let i = 0; i < q; i++) parts.push(id);
  }
  for (const c of deck.sideboard || []) {
    const id = idFrom(c);
    const q = qtyFrom(c);
    for (let i = 0; i < q; i++) parts.push(id);
  }
  return parts.join(' ');
}

export function parseTTS(text) {
  const deck = { mainDeck: [], battlefields: [], runes: [], sideboard: [] };
  const tokens = (text || '').trim().split(/\s+/).filter(Boolean);
  let i = 0;
  if (tokens[i]) deck.legend = { id: tokens[i++] };
  if (tokens[i]) deck.champion = { id: tokens[i++] };
  const byId = new Map();
  while (i < tokens.length) {
    const raw = tokens[i++];
    const base = raw.split('-').slice(0, 2).join('-');
    const prev = byId.get(base) || { id: base, quantity: 0 };
    prev.quantity++;
    byId.set(base, prev);
  }
  deck.mainDeck = [...byId.values()];
  return deck;
}

export function emitJSON(deck) {
  return JSON.stringify({
    name: deck.name,
    legend: deck.legend ? { id: idFrom(deck.legend) } : null,
    champion: deck.champion ? { id: idFrom(deck.champion) } : null,
    mainDeck: (deck.mainDeck || []).map((c) => ({ id: idFrom(c), quantity: qtyFrom(c) })),
    battlefields: (deck.battlefields || []).map((c) => ({ id: idFrom(c) })),
    runes: (deck.runes || []).map((c) => ({ id: idFrom(c), quantity: qtyFrom(c) })),
    sideboard: (deck.sideboard || []).map((c) => ({ id: idFrom(c), quantity: qtyFrom(c) })),
    extras: deck.extras || [],
    notes: deck.notes || ''
  }, null, 2);
}

export function parseJSON(json) {
  const o = typeof json === 'string' ? JSON.parse(json) : json;
  return {
    name: o.name,
    legend: o.legend ? { id: o.legend.id } : null,
    champion: o.champion ? { id: o.champion.id } : null,
    mainDeck: (o.mainDeck || []).map((c) => ({ id: c.id, quantity: c.quantity ?? 1 })),
    battlefields: (o.battlefields || []).map((c) => ({ id: c.id })),
    runes: (o.runes || []).map((c) => ({ id: c.id, quantity: c.quantity ?? 6 })),
    sideboard: (o.sideboard || []).map((c) => ({ id: c.id, quantity: c.quantity ?? 1 })),
    extras: o.extras || [],
    notes: o.notes || ''
  };
}

export function emitBase64(deck) {
  const json = emitJSON(deck);
  return btoa(unescape(encodeURIComponent(json)));
}

export function parseBase64(text) {
  try {
    const json = decodeURIComponent(escape(atob(text)));
    return parseJSON(json);
  } catch {
    return null;
  }
}
